import React, { FC, PropsWithChildren, ReactNode, useCallback, useEffect, useState } from "react"

import { useLocalStorage } from "@mantine/hooks"
import dayjs from "dayjs"
import updateLocale from "dayjs/plugin/updateLocale"
dayjs.extend(updateLocale)

import { LanguageCode, LanguageContext, Locator, Translations } from "./index"

export const LanguageProvider: FC<PropsWithChildren<{ appDefault: LanguageCode }>> = ({ appDefault, children }) => {
    const [code, setCode] = useLocalStorage<LanguageCode>({ key: "languageCode", defaultValue: appDefault })
    const [translations, setTranslations] = useState<Translations>()
    const updateTranslations = useCallback(
        async (code: LanguageCode) => setTranslations((await import(`../../assets/i18n/${code}.ts`)).default),
        [setTranslations],
    )

    const L = useCallback(
        ((id, ...variables: ReactNode[]) => {
            const base = translations?.[id] ?? id
            if (!variables.length) return base

            const out: ReactNode[] = [base]
            variables.forEach(variable => {
                const last = out.at(-1) as string
                if (last.includes("%s")) {
                    const [pre, post] = last.split(/%s(.*)/).slice(0, -1)
                    out.pop()
                    out.push(pre, variable, post)
                }
            })

            if (out.every(symbol => typeof symbol === "string" || typeof symbol === "number")) return out.join("")
            return out
        }) as Locator,
        [translations],
    )

    useEffect(() => {
        updateTranslations(code).catch(console.error)
        dayjs.updateLocale(code, { weekStart: 1 })
        dayjs.locale(code)
    }, [code, updateTranslations])

    return <LanguageContext.Provider value={{ L, code, setCode }}>{children}</LanguageContext.Provider>
}
